import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, XCircle, MoreVertical, Edit, Eye, Lock, Unlock, Trash2 } from 'lucide-react';
import type { User } from '@/types/user.types';
import { StatusBadge } from '@/components/common/StatusBadge';
import { UserAvatar } from '@/components/common/UserAvatar';
import { formatDateTime } from '@/utils/formatters';

interface UserTableRowProps {
  user: User;
  onViewDetails?: (id: string) => void;
  onEdit?: (id: string) => void;
  onBlock?: (id: string) => void;
  onUnblock?: (id: string) => void;
  onDelete?: (id: string) => void;
}

const roleLabels: Record<string, string> = {
  owner: 'Proprietário',
  manager: 'Gestor',
  foreman: 'Capataz',
  cowboy: 'Vaqueiro',
};

export function UserTableRow({
  user,
  onViewDetails,
  onEdit,
  onBlock,
  onUnblock,
  onDelete,
}: UserTableRowProps) {
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowActionsMenu(false);
      }
    };

    if (showActionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActionsMenu]);

  const handleToggleMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!showActionsMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const menuWidth = 224; // w-56 = 14rem = 224px
      const menuHeight = 200; // approximate height

      let top = rect.bottom + 4;
      let left = rect.right - menuWidth;

      // Check if menu goes below viewport
      if (top + menuHeight > window.innerHeight) {
        top = rect.top - menuHeight - 4;
      }

      // Check if menu goes beyond left edge
      if (left < 8) {
        left = 8;
      }

      setMenuPosition({ top, left });
    }

    setShowActionsMenu(!showActionsMenu);
  };
  return (
    <tr className="hover:bg-muted/50">
      <td className="w-[22%] px-6 py-4">
        <UserAvatar userId={user.id} name={user.name} phone={user.phone} />
      </td>
      <td className="w-[12%] px-6 py-4">
        <StatusBadge status={user.status} />
      </td>
      <td className="w-[12%] px-6 py-4">
        <p className="break-words text-text-secondary">{roleLabels[user.role] || user.role}</p>
      </td>
      <td className="w-[18%] px-6 py-4">
        <p className="break-words text-text-secondary">
          {typeof user.property === 'string' ? user.property : user.property?.name || 'N/A'}
        </p>
      </td>
      <td className="w-[16%] px-6 py-4">
        <p className="break-words text-sm text-text-title">{formatDateTime(user.lastAccess)}</p>
        {user.lastDevice && (
          <p className="break-words text-xs text-text-secondary">{user.lastDevice}</p>
        )}
      </td>
      <td className="w-[10%] px-6 py-4">
        {user.verified ? (
          <CheckCircle2 size={18} className="text-online" aria-label="Verificado" />
        ) : (
          <XCircle size={18} className="text-error" aria-label="Não verificado" />
        )}
      </td>
      <td className="w-[10%] px-6 py-4 text-right">
        <div className="relative inline-block">
          <button
            ref={buttonRef}
            onClick={handleToggleMenu}
            className="rounded-full p-2 hover:bg-muted"
          >
            <MoreVertical size={18} className="text-text-secondary" />
          </button>
          {showActionsMenu &&
            createPortal(
              <div
                ref={menuRef}
                className="fixed z-50 w-56 rounded-lg border border-border bg-card shadow-lg"
                style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
              >
                <button
                  onClick={() => {
                    onViewDetails?.(user.id);
                    setShowActionsMenu(false);
                  }}
                  className="flex w-full items-center gap-3 whitespace-nowrap px-4 py-2 text-left text-sm hover:bg-muted/50"
                >
                  <Eye size={16} />
                  Ver Detalhes
                </button>
                <button
                  onClick={() => {
                    onEdit?.(user.id);
                    setShowActionsMenu(false);
                  }}
                  className="flex w-full items-center gap-3 whitespace-nowrap px-4 py-2 text-left text-sm hover:bg-muted/50"
                >
                  <Edit size={16} />
                  Editar
                </button>
                {user.status === 'blocked' || user.status === 'suspended' ? (
                  <button
                    onClick={() => {
                      onUnblock?.(user.id);
                      setShowActionsMenu(false);
                    }}
                    className="flex w-full items-center gap-3 whitespace-nowrap px-4 py-2 text-left text-sm hover:bg-muted/50"
                  >
                    <Unlock size={16} />
                    Desbloquear
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      onBlock?.(user.id);
                      setShowActionsMenu(false);
                    }}
                    className="flex w-full items-center gap-3 whitespace-nowrap px-4 py-2 text-left text-sm hover:bg-muted/50"
                  >
                    <Lock size={16} />
                    Bloquear
                  </button>
                )}
                <button
                  onClick={() => {
                    onDelete?.(user.id);
                    setShowActionsMenu(false);
                  }}
                  className="flex w-full items-center gap-3 whitespace-nowrap px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={16} />
                  Excluir
                </button>
              </div>,
              document.body
            )}
        </div>
      </td>
    </tr>
  );
}
